import { defineCollection } from "@rebasepro/cms-types";

const usersCollection = defineCollection({
    name: "Users",
    singularName: "User",
    slug: "users",
    // Marks this as the auth collection. The admin injects `resetPasswordAction`
    // frontend-side for auth collections, which is why it is no longer imported
    // from @rebasepro/cms here — a UI import in a collection file drags React
    // into the backend's module graph at boot.
    auth: { enabled: true },
    table: "users",
    // Sin `schema`, así que vive en `public` como el resto de colecciones. Es lo
    // que hace que el arranque de auth mande SUS tablas a `rebase`:
    //   const authSchema = usersSchema === "public" ? "rebase" : usersSchema;
    // (packages/server-postgres/src/auth/ensure-tables.ts). Declararlo aquí como
    // "rebase" arrastraba todas las colecciones a ese esquema.
    securityRules: [
        { operation: "select", roles: ["admin"] },
        { operations: ["insert", "update", "delete"], roles: ["admin"] }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid",
            admin: {
                readOnly: true
            }
        },
        email: {
            name: "Email",
            type: "string",
            validation: {
                required: true,
                unique: true
            }
        },
        displayName: {
            name: "Name",
            type: "string",
            columnName: "display_name",
            validation: {
                required: true
            }
        },
        photoURL: {
            name: "Photo URL",
            type: "string",
            columnName: "photo_url",
            url: true,
            admin: { urlPreview: "image" }
        },
        roles: {
            name: "Roles",
            type: "array",
            columnType: "text[]",
            of: {
                name: "Role",
                type: "string",
                enum: {
                    admin: "Admin",
                    editor: "Editor",
                    viewer: "Viewer"
                }
            }
        },
        passwordHash: {
            name: "Password Hash",
            type: "string",
            columnName: "password_hash",
            excludeFromApi: true,
            admin: {
                hideFromCollection: true,
                disabled: { hidden: true }
            }
        },
        emailVerified: {
            name: "Email Verified",
            type: "boolean",
            columnName: "email_verified",
            defaultValue: false,
            admin: {
                hideFromCollection: true,
                disabled: { hidden: true }
            }
        },
        emailVerificationToken: {
            name: "Email Verification Token",
            type: "string",
            columnName: "email_verification_token",
            excludeFromApi: true,
            admin: {
                hideFromCollection: true,
                disabled: { hidden: true }
            }
        },
        emailVerificationSentAt: {
            name: "Email Verification Sent At",
            type: "date",
            columnName: "email_verification_sent_at",
            admin: {
                hideFromCollection: true,
                disabled: { hidden: true }
            }
        },
        metadata: {
            name: "Metadata",
            type: "map",
            defaultValue: {},
            admin: {
                hideFromCollection: true,
                disabled: { hidden: true }
            }
        },
        createdAt: {
            name: "Created At",
            type: "date",
            columnName: "created_at",
            autoValue: "on_create",
            admin: {
                readOnly: true
            }
        },
        updatedAt: {
            name: "Updated At",
            type: "date",
            columnName: "updated_at",
            autoValue: "on_update",
            admin: {
                hideFromCollection: true,
                disabled: { hidden: true }
            }
        }
    },
    admin: {
        icon: "Users",
        group: "Settings",
        openEntityMode: "dialog",
        disableDefaultActions: ["copy"],
        defaultViewMode: "list",
        enabledViews: ["list", "table"],
        // Who the account belongs to, then what it can do. The email is the
        // identity here, so it sits in the preview and not also in a column.
        listProperties: ["roles", "emailVerified", "createdAt"],
        previewProperties: ["displayName", "email"],
        sort: ["createdAt", "desc"],
        propertiesOrder: [
            "id",
            "email",
            "displayName",
            "roles",
            "createdAt"
        ]
    }
});

export default usersCollection;
